// Offline floor resolution against the PowerSync local SQLite mirror.
//
// Mirror of the online POST /capture/floors/resolve flow
// (apps/backend/src/capture/resolve-floor.ts) for builds where that
// endpoint is unreachable. The "+ New Room" dialog picks a floor name
// from the admin-curated, contract-scoped floor_options list; this maps
// that name to a stable per-building `floors` row id, creating the row
// lazily on first use at the building so the optimistic space can FK to
// it before sync.
//
// The id is generated on-device (crypto.randomUUID) and reused by the
// connector when it POSTs to /capture/floors/resolve on reconnect — the
// backend honours the client-supplied id, so the server row, this local
// row, and the dependent space FK all share one id. The (building_id,
// name) find-first below collapses duplicates the same way the server's
// unique index does; the operational policy "one surveyor per building
// at a time" (PRD) excludes the concurrent same-(building, name) racer
// that would otherwise diverge. If that policy is ever broken the
// divergence degrades to a LOUD space-upload FK failure (the space PUT
// references a floor id the server replaced), never a silent drop.
//
// Floor NAMES remain admin-curated reference data (invariant #5). This
// does NOT let field staff invent floors — it records an instance of an
// already-approved option against the building, exactly as the online
// resolve does, and the server re-validates the name against
// floor_options when the floor PUT reaches the upload queue.

import type { ResolvedFloor } from '../use-floor-options'

import { db } from './client'

// Minimal read/write surface so the core is unit-testable with a fake;
// the real PowerSyncDatabase singleton satisfies this.
export interface LocalResolveFloorDb {
  getAll<T>(sql: string, parameters?: unknown[]): Promise<T[]>
  execute(sql: string, parameters?: unknown[]): Promise<unknown>
}

interface SessionLookupRow {
  buildingId: string | null
}

interface FloorLookupRow {
  id: string
  name: string
}

// Wrapped for testability: tests inject a fixed id generator so the
// created floor id is deterministic. The runtime path uses
// crypto.randomUUID() directly (v4, consistent with spaces / items).
export function generateFloorUuid(): string {
  return crypto.randomUUID()
}

// Testable core — takes the db (and id generator) so unit tests can pass
// fakes.
export async function resolveFloorLocalWithDb(
  database: LocalResolveFloorDb,
  sessionId: string,
  floorName: string,
  generateId: () => string = generateFloorUuid,
): Promise<ResolvedFloor> {
  const trimmed = floorName.trim()
  if (trimmed.length === 0) {
    throw new Error(
      'resolveFloorLocal: floorName is empty — the New Room picker only ' +
        'offers admin-curated floor options, so an empty name is a routing bug.',
    )
  }

  // Recover the building from the active session, mirroring
  // dispatchCreateSpace's invariant: rooms (and therefore floors) can
  // only be resolved on a building-scoped session that exists locally.
  const sessionRows = await database.getAll<SessionLookupRow>(
    `SELECT building_id AS buildingId
     FROM capture_sessions
     WHERE id = ?
     LIMIT 1`,
    [sessionId],
  )
  const session = sessionRows[0]
  if (!session) {
    throw new Error(
      `resolveFloorLocal: capture session ${sessionId} not found in local ` +
        `PowerSync mirror — the user must have an active session before ` +
        `resolving a floor (routing bug, not a data race).`,
    )
  }
  if (session.buildingId === null || session.buildingId === undefined) {
    throw new Error(
      `resolveFloorLocal: capture session ${sessionId} is site-scoped ` +
        `(buildingId is null); floors can only be resolved on a ` +
        `building-scoped session.`,
    )
  }
  const buildingId = session.buildingId

  // Find-first on (building_id, name) — mirrors the server's
  // find-or-create. Returns the existing row so repeated picks of the
  // same floor at the same building collapse to one id (and one space FK
  // target).
  const existing = await database.getAll<FloorLookupRow>(
    `SELECT id, name FROM floors
     WHERE building_id = ? AND name = ?
     LIMIT 1`,
    [buildingId, trimmed],
  )
  const found = existing[0]
  if (found) {
    return { floorId: found.id, floorName: found.name }
  }

  // Create lazily. display_order 0 matches the server insert
  // (resolve-floor.ts); admin reorder is authoritative and streams down
  // via reference_global, overwriting this provisional 0 when the synced
  // row arrives.
  const floorId = generateId()
  const nowIso = new Date().toISOString()
  await database.execute(
    `INSERT INTO floors (id, building_id, name, display_order, created_at, updated_at)
     VALUES (?, ?, ?, 0, ?, ?)`,
    [floorId, buildingId, trimmed, nowIso, nowIso],
  )
  // Stamp the floor's origin session in the device-local floor_origin_sessions
  // table (NOT a synced column — see schema.ts). Only on the lazy CREATE: a
  // find-first hit reuses an existing floor whose origin is already stamped (or
  // a synced reference floor that never re-enters the PUT dispatch). The
  // connector's floors PUT reads this back to drop a cancelled-origin floor
  // instead of wedging the queue (H1/#693), while still recovering a floor whose
  // session merely 409-re-homed across batches (C2/#690). Written AFTER the
  // floors INSERT so the existing INSERT shape stays executeCalls[0]; an interrupt
  // between the two degrades to the unstamped building-lookup path (the prior
  // behaviour), never a wedge.
  //
  // PLAIN INSERT, NOT an upsert: PowerSync exposes every table (local-only
  // included) as a SQLite VIEW, and SQLite rejects `INSERT … ON CONFLICT` on a
  // view with "cannot UPSERT a view" — which threw here synchronously and broke
  // ALL field room creation (the dialog awaits this resolve before createSpace).
  // An upsert is also unnecessary: `floorId` was just minted by generateId() and
  // this branch is reached only after the (building, name) find-first MISSED, so
  // no floor_origin_sessions row can already carry this id. A re-pick of the
  // same floor hits the find-first early-return above and never re-stamps.
  await database.execute(
    `INSERT INTO floor_origin_sessions (id, origin_session_id)
     VALUES (?, ?)`,
    [floorId, sessionId],
  )
  return { floorId, floorName: trimmed }
}

// Production wrapper matching the resolveFloorOption signature so the
// build-time seam can route to either without the dialog branching.
export function resolveFloorLocal(
  sessionId: string,
  floorName: string,
): Promise<ResolvedFloor> {
  return resolveFloorLocalWithDb(db, sessionId, floorName)
}
